import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { isSymphonyAdmin } from 'helpers/admin';
import { UserData, UserMetadata } from 'modules/Types';
import * as Sentry from '@sentry/browser';
import { identify, registerOnce, setUserPropertiesOnce } from '../analytics';
import updateUserMetadata_api, { deleteUser_api, getUserByAuth0Id_api } from 'api/userApi';
import Logger from 'Logger';
import { LogoutOptions } from '@auth0/auth0-react';
import useBrand, { BrandExistsState, getAnonymousSessionIdFromLocalStorage, removeAnonymousSessionIdFromLocalStorage, removeBrandFromLocalStorage } from './useBrand';
import { UnauthenticatedAxios } from 'helpers/Interceptor';
import Airtable from 'airtable';
import { getSearchParam, setCookie } from 'modules/Utils'

/** Custom error class for auth-related errors */
export class AuthError extends Error {
    /** Whether this error should trigger a logout */
    public isLogout: boolean;

    constructor(message: string, isLogout: boolean = true) {
        super(message);
        this.isLogout = isLogout;
        this.name = 'AuthError';
    }
}

interface UserTrackingType {
    utmParams?: { [key: string]: string };
    partnershipParams?: { [key: string]: string };
    rewardfulParams?: { [key: string]: string };
}

interface UserState {
    /** Current user data */
    currentUser: UserData | undefined;
    isLoadingUser: boolean;
    isLoadingOrganizationFromAdminView: boolean;
    isSymphonyAdmin: boolean;
    isSymphonyBetaTestUser: boolean;

    /** Actions */
    setCurrentUser: (user: UserData) => void;

    /** Computed values */
    proFreeTrialRedeemed: () => boolean;
    liteFreeTrialRedeemed: () => boolean;

    /** Methods */
    getUser: (userSubAuth0Id: string | undefined) => Promise<UserData | undefined>;
    getCurrentUser: (
        isAuthenticatedWithAuth0: boolean,
        userSubAuth0Id: string,
        getAccessTokenSilently: () => Promise<string>,
        logout: (options: LogoutOptions) => void,
    ) => Promise<UserData | undefined>;
    deleteUser: (userId: number, logout?: (options: LogoutOptions) => void) => Promise<void>;
    processCookies: () => UserTrackingType;
    setUserTrackingMetadata: (trackingDetails: UserTrackingType, user: UserData) => Promise<{
        updated: boolean,
        user: UserData
    }>;

    /** Passwordless signup methods */
    checkUserExistsInDatabase: (auth0UserId: string) => Promise<UserData | null>;
    completePasswordlessSignup: (params: {
        email: string;
        authResult: any;
        paymentIntentId?: string;
        planType?: string;
        source: string;
    }) => Promise<{ user: UserData; brand: any }>;
    setupAuthenticationState: (authResult: any) => void;
    handleExistingUserLogin: (authResult: any, user: UserData) => Promise<{ user: UserData }>;

    checkSymphonyBetaTestUserStatus: () => void;

    /**
     * Detects referral parameters from URL and saves them to cookies
     * This should be called on page load to capture referral tracking
     */
    trackReferralFromUrl: (url?: string) => void;

    /**
     * Checks if current user came from a referral
     */
    hasReferral: () => { inCookies: boolean; inURL: boolean };
}

export interface UserExistsState extends UserState {
    currentUser: UserData;
}

const useUser = create(immer<UserState>((set, get) => ({
    currentUser: undefined,
    isLoadingUser: false,
    organization: undefined,
    isLoadingOrganizationFromAdminView: false,
    isSymphonyAdmin: false,
    isSymphonyBetaTestUser: false,
    setCurrentUser: (user: UserData) => {
        set({
            currentUser: user,
            isSymphonyAdmin: isSymphonyAdmin(user),
        });
    },

    getUser: async (userSubAuth0Id: string | undefined) => {
        if (!userSubAuth0Id) return;

        set({ isLoadingUser: true });

        try {
            const userData = await getUserByAuth0Id_api(userSubAuth0Id);

            set(state => {
                state.currentUser = userData;

                // if (userData?.organization && userData?.metadata?.organization_subscription?.status !== 'canceled') {
                //     state.organization = userData.organization;
                // }

                state.isLoadingUser = false;
            });

            return userData;
        } catch (error) {
            set({ isLoadingUser: false });
            throw error;
        }
    },

    /**
     * Detects referral parameters from URL and saves them to cookies
     * This should be called on page load to capture referral tracking
     */
    trackReferralFromUrl: (url?: string): void => {
        try {
            const viaParam = getSearchParam({ 
                name: 'via', 
                search: url || window.location.href 
            });

            if (viaParam) {
                setCookie('rewardful_via', viaParam, {
                    days: 30,
                    path: '/',
                    secure: true,
                    sameSite: 'Lax'
                });

                console.log('🎯 Saved referral tracking:', { rewardful_via: viaParam });
                
                // Also trigger Rewardful's own tracking if available
                if (typeof window !== 'undefined' && (window as any).rewardful) {
                    (window as any).rewardful('ready', () => {
                        console.log('📊 Rewardful tracking initialized for referral');
                    });
                }
            }
        } catch (error) {
            console.error('Error tracking referral from URL:', error);
        }
    },

    /**
     * Checks if current user came from a referral
     */
    hasReferral: (): { inCookies: boolean; inURL: boolean } => {
        if (typeof document === 'undefined') {
            return { inCookies: false, inURL: false };
        }
        
        // Check cookies for saved referral
        let inCookies = false;
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
            const [key, value] = cookie.split('=').map(s => s.trim());
            if (key === 'rewardful_via') {
                inCookies = !!value;
                break;
            }
        }
        
        // Check current URL for via parameter
        const urlParams = new URLSearchParams(window.location.search);
        const inURL = !!urlParams.get('via');
        
        return { inCookies, inURL };
    },

    getCurrentUser: async (
        isAuthenticatedWithAuth0: boolean,
        userSubAuth0Id: string,
        getAccessTokenSilently: () => Promise<string>,
        logout: (options: LogoutOptions) => void,
    ): Promise<UserData | undefined> => {
        const {
            isLoadingUser,
            currentUser,
            setCurrentUser,
            setUserTrackingMetadata,
            checkSymphonyBetaTestUserStatus,
            processCookies,
            trackReferralFromUrl
        } = get();

        if (isLoadingUser || currentUser) {
            return currentUser;
        }

        try {
            if (!isAuthenticatedWithAuth0) { // NOTE: This NEVER will happen, but it's here as a failsafe
                throw new AuthError('User is not authenticated by Auth0');
            }

            set({ isLoadingUser: true });

            // TODO: JOIN_PAGE.md ✅ Track referral from URL before processing cookies
            // trackReferralFromUrl();

            const token = await getAccessTokenSilently();
            if (!handleAuthToken(token)) {
                throw new AuthError('Could not get auth0 access token silently');
            }

            const currentUserData = await getUserByAuth0Id_api(userSubAuth0Id);

            const trackingDetails = processCookies();

            identify({
                id: String(currentUserData.userid),
                email: currentUserData.email || undefined,
                name: undefined
            });

            setCurrentUser(currentUserData);
            const { updated, user } = await setUserTrackingMetadata(trackingDetails, currentUserData);
            checkRewardfulStatus();
            await checkSymphonyBetaTestUserStatus();

            if (updated) {
                setCurrentUser(user)
            }

            return currentUserData;
        } catch (e) {
            Logger.error(e);
            if ((e as AuthError)) {
                clearSessionDataOnLogout();
                // logout({});
            }
        } finally {
            set({ isLoadingUser: false });
        }
    },

    deleteUser: async (userId: number, logout?: (options: LogoutOptions) => void) => {
        try {
            set({ isLoadingUser: true });
            await deleteUser_api(userId);
            clearSessionDataOnLogout();
            logout && logout({});
        } catch (error) {
            Logger.error(error);
            throw error;
        } finally {
            set({ isLoadingUser: false });
        }
    },

    processCookies: () => {
        const savedObject: UserTrackingType = {
            utmParams: {},
            partnershipParams: {},
            rewardfulParams: {},
        };

        document.cookie.split(';').forEach(cookieStr => {
            const [key, value] = cookieStr.split('=').map(str => str.trim());

            if (key.startsWith('utm_')) {
                savedObject.utmParams![key] = value;
            }
            if (key === 'referrer') {
                savedObject.partnershipParams![key] = value;
            }
            if (key === 'rewardful_via' || key === 'rewardful_affiliate_id') {
                savedObject.rewardfulParams![key] = value;
            }
        });

        if (Object.keys(savedObject.utmParams!).length === 0) {
            delete savedObject.utmParams;
        }
        if (Object.keys(savedObject.partnershipParams!).length === 0) {
            delete savedObject.partnershipParams;
        }
        if (Object.keys(savedObject.rewardfulParams!).length === 0) {
            delete savedObject.rewardfulParams;
        }

        return savedObject;
    },

    setUserTrackingMetadata: async (trackingDetails: UserTrackingType, user: UserData): Promise<{
        updated: boolean,
        user: UserData
    }> => {
        const { utmParams, partnershipParams, rewardfulParams } = trackingDetails;
        const existingMetadata: UserMetadata | null = user.metadata || null;
        let metadataUpdateNeeded = false;

        const newMetadata: UserMetadata = {
            ...existingMetadata,
        } as UserMetadata;

        const existingReferrer = existingMetadata?.affiliate_referrer;
        if (partnershipParams?.referrer && !existingReferrer) {
            newMetadata.affiliate_referrer = partnershipParams.referrer;
            metadataUpdateNeeded = true;
        }

        const existingAttribution = existingMetadata && existingMetadata.attribution;
        if (utmParams && Object.keys(utmParams).length > 0 && !existingAttribution) {
            newMetadata.attribution = utmParams;
            metadataUpdateNeeded = true;
        }

        if (utmParams && Object.keys(utmParams).length > 0 && existingAttribution) {
            newMetadata.last_touch_attribution = utmParams;
            metadataUpdateNeeded = true;
        }

        if (rewardfulParams && !existingReferrer && (existingMetadata?.rewardful?.rewardful_via !== rewardfulParams.rewardful_via)) {
            newMetadata.rewardful = rewardfulParams;
            metadataUpdateNeeded = true;
        }

        let updatedUser = user;
        let updated = false;
        if (metadataUpdateNeeded) {
            updatedUser = await updateUserMetadata_api(user.userid, newMetadata);
            updated = true;
        }

        const savedObject: { [key: string]: any } = {
            ...newMetadata,
        };

        if (newMetadata.attribution) {
            for (const key in newMetadata.attribution) {
                let words = key.split('_');
                words = words.map(word => word.charAt(0).toUpperCase() + word.slice(1));
                const formattedKey = 'Initial ' + words.join(' ');

                savedObject[key] = (newMetadata.attribution as any)[key];
                savedObject[formattedKey] = (newMetadata.attribution as any)[key];
            }
        }

        if (newMetadata.affiliate_referrer) {
            savedObject['affiliate_referrer'] = newMetadata.affiliate_referrer;
            savedObject['Referred by Partner'] = newMetadata.affiliate_referrer;
        }

        if (newMetadata.rewardful) {
            savedObject['rewardful_via'] = newMetadata.rewardful.rewardful_via;
            savedObject['Was Referred by Other User'] = true;
        } else {
            savedObject['Was Referred by Other User'] = false;
        }

        registerOnce({ ...savedObject });
        setUserPropertiesOnce(savedObject);

        return { updated, user: updatedUser };
    },

    proFreeTrialRedeemed: () => {
        const currentUser = get().currentUser;
        const { currentBrand } = useBrand.getState() as BrandExistsState;
        return Boolean(currentUser?.metadata?.free_trial_redeemed) || Boolean(currentBrand?.subscription?.freeTrialOver);
    },

    liteFreeTrialRedeemed: () => {
        const currentUser = get().currentUser;
        const { currentBrand } = useBrand.getState() as BrandExistsState;
        return true
    },

    checkSymphonyBetaTestUserStatus: async () => {
        const currentUser = get().currentUser;
        if (!currentUser) {
            return false;
        }

        try {
            // Initialize Airtable with API key from environment
            const base = new Airtable({ apiKey: process.env.REACT_APP_AIRTABLE_API_KEY }).base('applMNwCYrCwNp854');

            // Fixed: Properly quote both values as strings
            const records = await base('Table 1').select({
                filterByFormula: `OR({fldvU1DqSJJ0NWRmw} = '${currentUser.email}', {fld1BkYh6SChJAXLb} = '${currentUser.userid}')`,
                maxRecords: 1
            }).firstPage();

            const isBetaTestUser = records.length > 0;

            set({ isSymphonyBetaTestUser: isBetaTestUser });
            return isBetaTestUser;
        } catch (error) {
            Logger.error('Error checking Symphony Test User status:', error);
            return false;
        }
    },

    checkUserExistsInDatabase: async (auth0UserId: string): Promise<UserData | null> => {
        try {
            const userData = await getUserByAuth0Id_api(auth0UserId);
            return userData;
        } catch (error: any) {
            if (error.response?.status === 404) {
                // User doesn't exist - this is expected for new signups
                return null;
            }
            // Re-throw other errors
            throw error;
        }
    },

    completePasswordlessSignup: async (params: {
        email: string;
        authResult: any;
        paymentIntentId?: string;
        planType?: string;
        source: string;
    }): Promise<{ user: UserData; brand: any }> => {
        try {
            // Get the temporary session ID for migration
            const temporarySessionId = getAnonymousSessionIdFromLocalStorage();
            
            // Call the signup completion endpoint using UnauthenticatedAxios
            const response = await UnauthenticatedAxios.post('/signup/complete', {
                email: params.email.toLowerCase().trim(),
                authResult: params.authResult,
                paymentIntentId: params.paymentIntentId,
                planType: params.planType,
                source: params.source,
                temporarySessionId: temporarySessionId
            });

            const { brand, user } = response.data;

            // Set up authentication state
            get().setupAuthenticationState(params.authResult);

            // Set current user in the store
            set({ currentUser: user });

            // Set up the brand in the useBrand store
            const { setCurrentBrand } = useBrand.getState();
            setCurrentBrand(brand);

            // Clean up temporary session ID since migration is handled on backend
            removeAnonymousSessionIdFromLocalStorage()

            return { user, brand };
        } catch (error: any) {
            console.error('Failed to complete passwordless signup:', error);
            
            // Better error handling with UnauthenticatedAxios error structure
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            throw new Error(`Failed to create account: ${errorMessage}`);
        }
    },

    setupAuthenticationState: (authResult: any) => {
        // Set up localStorage tokens
        if (authResult.accessToken) {
            localStorage.setItem('accessToken', authResult.accessToken);
        }
        if (authResult.idToken) {
            localStorage.setItem('idToken', authResult.idToken);
        }
        
        // Additional auth setup could go here
        console.log('Authentication state set up for user:', authResult.sub);
    },

    handleExistingUserLogin: async (authResult: any, user: UserData) => {
        try {
            // Set up authentication state
            get().setupAuthenticationState(authResult);
            
            // Set current user in the store
            set({ currentUser: user });
            
            // Handle temporary brand linking if there's an anonymous session
            const temporarySessionId = getAnonymousSessionIdFromLocalStorage();
            if (temporarySessionId) {
                try {
                    // Link the anonymous brand directly to the user
                    const response = await UnauthenticatedAxios.post('/temporary-brand/link-to-user', {
                        sessionId: temporarySessionId,
                        userId: user.id
                    });
                    
                    if (response.data?.brand) {
                        console.log('✅ Successfully linked anonymous brand to existing user');
                        // Clean up the session ID
                        removeAnonymousSessionIdFromLocalStorage();
                    }
                } catch (linkingError) {
                    console.error('Failed to link anonymous brand to existing user:', linkingError);
                    // Don't fail the login if linking fails
                }
            }
            
            return { user };
        } catch (error: any) {
            console.error('Failed to handle existing user login:', error);
            throw new Error(`Login failed: ${error.message}`);
        }
    }
}))
);

/** Helper function to handle auth token */
const handleAuthToken = (token: string): boolean => {
    if (!token) {
        Sentry.captureException('Could not get auth0 access token silently');
        return false;
    }
    localStorage.setItem('accessToken', token);
    return true;
};

/** Helper function to clear session data on logout */
export const clearSessionDataOnLogout = () => {
    // Clear all localStorage (as was done before)
    localStorage.clear();
    
    console.log('✅ Cleared session data on logout');
};

const checkRewardfulStatus = () => {
    const rewardful = (window as any).rewardful;
    if (rewardful) {
        rewardful('ready', () => {
            const Rewardful = (window as any).Rewardful;
            if (Rewardful.referral) {
                Logger.debug('Current referral ID: ', Rewardful.referral);
            } else {
                Logger.warn('No referral present.');
            }
        });
    }
};

export default useUser;
